// ─────────────────────────────────────────────────────────────────────────────
// Cache des appels au cube : une requête identique n'est envoyée qu'une fois.
//   - en cours  : les appelants simultanés partagent la même requête HTTP
//   - terminée  : le résultat est réutilisé jusqu'à expiration (ttlMs)
//   - échec     : jamais mis en cache, l'appel suivant réessaie
// Annulation : la requête partagée n'est annulée que si TOUS ses appelants
// ont annulé ; un appelant qui annule reçoit seul une AbortError.
// ─────────────────────────────────────────────────────────────────────────────

interface Entry<T> {
  promise: Promise<T>;
  controller: AbortController;
  /** Appelants en attente (non annulés) d'une requête en cours. */
  waiting: number;
  settled: boolean;
  expiresAt: number;
}

export interface RequestCacheOptions {
  /** Durée de validité d'un résultat (ms). Infinity = toute la session. */
  ttlMs: number;
  /** Nombre max de résultats conservés (les plus anciens sont évincés). */
  maxEntries?: number;
}

const abortError = (): Error => {
  const err = new Error("aborted");
  err.name = "AbortError";
  return err;
};

export function createRequestCache<T>({ ttlMs, maxEntries = 100 }: RequestCacheOptions) {
  const entries = new Map<string, Entry<T>>();

  const evict = () => {
    const now = Date.now();
    entries.forEach((e, k) => {
      if (e.settled && e.expiresAt <= now) entries.delete(k);
    });
    // Map conserve l'ordre d'insertion : on évince les plus anciennes
    for (const k of entries.keys()) {
      if (entries.size <= maxEntries) break;
      entries.delete(k);
    }
  };

  function get(key: string, run: (signal: AbortSignal) => Promise<T>, signal?: AbortSignal): Promise<T> {
    if (signal?.aborted) return Promise.reject(abortError());

    let entry = entries.get(key);
    if (entry && entry.settled && entry.expiresAt <= Date.now()) {
      entries.delete(key);
      entry = undefined;
    }
    if (!entry) {
      const controller = new AbortController();
      const created = { controller, waiting: 0, settled: false, expiresAt: Infinity } as Entry<T>;
      created.promise = run(controller.signal).then(
        (value) => {
          created.settled = true;
          created.expiresAt = Date.now() + ttlMs;
          return value;
        },
        (err) => {
          if (entries.get(key) === created) entries.delete(key);
          throw err;
        },
      );
      // évite un "unhandled rejection" si tous les appelants ont annulé
      created.promise.catch(() => {});
      entries.set(key, created);
      evict();
      entry = created;
    }

    if (entry.settled) return entry.promise;

    const e = entry;
    e.waiting++;
    // sans signal, l'appelant ne peut pas annuler : il garde la requête en vie
    if (!signal) return e.promise;
    return new Promise<T>((resolve, reject) => {
      const onAbort = () => {
        e.waiting--;
        if (e.waiting === 0 && !e.settled) {
          if (entries.get(key) === e) entries.delete(key);
          e.controller.abort();
        }
        reject(abortError());
      };
      signal.addEventListener("abort", onAbort, { once: true });
      e.promise.then(
        (v) => {
          signal.removeEventListener("abort", onAbort);
          resolve(v);
        },
        (err) => {
          signal.removeEventListener("abort", onAbort);
          reject(err);
        },
      );
    });
  }

  return {
    get,
    /** Vide le cache (ex. après retraitement du cube ou déconnexion). */
    clear: () => entries.clear(),
  };
}

/** Clé stable d'un objet JSON (ordre des propriétés indifférent). */
export function stableKey(value: unknown): string {
  return JSON.stringify(value, (_k, v) =>
    v && typeof v === "object" && !Array.isArray(v)
      ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, v[k]]))
      : v,
  );
}
